import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/AuthProvider';
import { Save, DollarSign, Calendar, Target, Clock, Key, CheckCircle, Facebook, Instagram, Settings as SettingsIcon, Edit, RefreshCw, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import AccountMetaAdsModal from './AccountMetaAdsModal';
import AdvancedTargetingModal from './AdvancedTargetingModal';
import WooCommerceSettings from './WooCommerceSettings';

interface TargetingConfig {
  age_min: number;
  age_max: number;
  genders: number[];
  geo_locations: {
    countries: string[];
    cities?: string[];
    regions?: string[];
  };
  interests?: any[];
  behaviors?: any[];
}

interface Settings {
  auto_promote: boolean;
  default_budget_daily: number;
  default_duration_days: number;
  phase1_target_type: string;
  phase2_target_type: string;
  phase1_targeting: TargetingConfig;
  phase2_targeting: TargetingConfig;
  delay_hours: number;
  meta_app_id: string;
  meta_app_secret: string;
  meta_ad_account_id: string;
  meta_access_token: string;
  meta_token_expires_at?: string;
  woo_url: string;
  woo_consumer_key: string;
  woo_consumer_secret: string;
  autoscale_enabled: boolean;
  anthropic_key: string;
  image_api_key: string;
}

interface SocialAccount {
  id: string;
  platform: string;
  page_name: string;
  page_id: string;
  auto_boost_enabled: boolean;
  status: string;
  meta_app_id?: string | null;
  meta_app_secret?: string | null;
  meta_ad_account_id: string | null;
  meta_access_token: string | null;
  access_token: string | null;
  meta_token_expires_at?: string | null;
}

interface AdsBoosterSettingsProps {
  onUpdate: () => void;
}

export default function AdsBoosterSettings({ onUpdate }: AdsBoosterSettingsProps) {
  const { user } = useAuth();
  const defaultTargeting: TargetingConfig = {
    age_min: 18,
    age_max: 65,
    genders: [0],
    geo_locations: {
      countries: ['PL'],
    },
  };

  const [settings, setSettings] = useState<Settings>({
    auto_promote: false,
    default_budget_daily: 5.0,
    default_duration_days: 7,
    phase1_target_type: 'page_fans',
    phase2_target_type: 'lookalike',
    phase1_targeting: defaultTargeting,
    phase2_targeting: defaultTargeting,
    delay_hours: 24,
    meta_app_id: '',
    meta_app_secret: '',
    meta_ad_account_id: '',
    meta_access_token: '',
    woo_url: '',
    woo_consumer_key: '',
    woo_consumer_secret: '',
    autoscale_enabled: false,
    anthropic_key: '',
    image_api_key: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<SocialAccount | null>(null);
  const [targetingModalPhase, setTargetingModalPhase] = useState<1 | 2 | null>(null);

  useEffect(() => {
    if (user?.id) {
      fetchSettings();
      fetchSocialAccounts();
    }
  }, [user?.id]);

  const fetchSettings = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('ad_booster_settings')
        .select('*')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        setSettings({
          auto_promote: data.auto_promote,
          default_budget_daily: data.default_budget_daily,
          default_duration_days: data.default_duration_days,
          phase1_target_type: data.phase1_target_type,
          phase2_target_type: data.phase2_target_type,
          phase1_targeting: data.phase1_targeting || defaultTargeting,
          phase2_targeting: data.phase2_targeting || defaultTargeting,
          delay_hours: data.delay_hours,
          meta_app_id: data.meta_app_id || '',
          meta_app_secret: data.meta_app_secret || '',
          meta_ad_account_id: data.meta_ad_account_id || '',
          meta_access_token: data.meta_access_token || '',
          meta_token_expires_at: data.meta_token_expires_at,
          woo_url: data.woo_url || '',
          woo_consumer_key: data.woo_consumer_key || '',
          woo_consumer_secret: data.woo_consumer_secret || '',
          autoscale_enabled: data.autoscale_enabled || false,
          anthropic_key: data.anthropic_key || '',
          image_api_key: data.image_api_key || '',
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Błąd podczas ładowania ustawień');
    } finally {
      setLoading(false);
    }
  };

  const [importing, setImporting] = useState(false);

  const handleImportFromSocial = async () => {
    // Token is stored in `access_token` (from SMS scheduler) or `meta_access_token` (from Ads config)
    const fbAccount = socialAccounts.find(acc => acc.platform === 'facebook' && (acc.access_token || acc.meta_access_token));
    if (!fbAccount) {
      toast.error('Brak ważnego konta Facebook w systemie. Podłącz konto w SMS Harmonogramie.');
      return;
    }

    const token = fbAccount.meta_access_token || fbAccount.access_token;
    if (!token) {
      toast.error('Token jest pusty.');
      return;
    }

    setImporting(true);
    toast.loading('Wykrywanie danych z Meta API...', { id: 'import' });

    const imported: string[] = [];
    const updates: Partial<Settings> = {
      meta_access_token: token,
    };
    imported.push('Token');

    try {
      // Use edge function to avoid CORS - call test-meta-ads-token which does all detection server-side
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const testResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-meta-ads-token`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              meta_access_token: token,
              meta_app_id: settings.meta_app_id || '',
              meta_app_secret: settings.meta_app_secret || '',
            }),
          }
        );

        if (testResponse.ok) {
          const testResult = await testResponse.json();

          // Extract App ID from token debug
          if (testResult.token_info?.app_id) {
            updates.meta_app_id = testResult.token_info.app_id;
            imported.push('App ID');
          }

          // Extract token expiration
          if (testResult.token_info?.expires_at && testResult.token_info.expires_at > 0) {
            updates.meta_token_expires_at = new Date(testResult.token_info.expires_at * 1000).toISOString();
            imported.push('Data wygaśnięcia');
          }

          // Extract Ad Account ID
          if (testResult.ad_account_id) {
            updates.meta_ad_account_id = testResult.ad_account_id;
            imported.push(`Konto Reklamowe (${testResult.ad_account_name || testResult.ad_account_id})`);
          }

          // Extract from available accounts if no direct match
          if (!updates.meta_ad_account_id && testResult.tests?.available_accounts?.data?.data?.length > 0) {
            const activeAcc = testResult.tests.available_accounts.data.data.find((a: any) => a.account_status === 1) || testResult.tests.available_accounts.data.data[0];
            if (activeAcc?.id) {
              updates.meta_ad_account_id = activeAcc.id;
              imported.push(`Konto Reklamowe (${activeAcc.name || activeAcc.id})`);
            }
          }
        }
      }

      // Apply all updates
      setSettings(prev => ({ ...prev, ...updates }));

      const missing: string[] = [];
      if (!updates.meta_app_id && !settings.meta_app_id) missing.push('App ID');
      if (!updates.meta_ad_account_id && !settings.meta_ad_account_id) missing.push('Ad Account');
      if (!settings.meta_app_secret) missing.push('App Secret');

      if (missing.length > 0) {
        toast.success(`Zaimportowano: ${imported.join(', ')}.\nUzupełnij ręcznie: ${missing.join(', ')}`, { id: 'import', duration: 6000 });
      } else {
        toast.success(`Wszystko zaimportowane! (${imported.join(', ')})`, { id: 'import' });
      }
    } catch (error) {
      console.error('Import error:', error);
      // Even if API calls fail, we still imported the token
      setSettings(prev => ({ ...prev, ...updates }));
      toast.success(`Zaimportowano token z konta: ${fbAccount.page_name}`, { id: 'import' });
    } finally {
      setImporting(false);
    }
  };

  const fetchSocialAccounts = async () => {
    try {
      const { data, error } = await supabase
        .from('social_accounts')
        .select('id, platform, page_name, page_id, auto_boost_enabled, status, meta_app_id, meta_app_secret, meta_ad_account_id, meta_access_token, access_token, meta_token_expires_at')
        .eq('owner_user_id', user?.id)
        .in('platform', ['facebook', 'instagram'])
        .eq('status', 'active')
        .order('platform')
        .order('page_name');

      if (error) throw error;

      setSocialAccounts(data || []);
    } catch (error) {
      console.error('Error fetching social accounts:', error);
      toast.error('Błąd podczas ładowania kont');
    }
  };

  const handleAccountMetaAdsSave = (accountId: string, adAccountId: string | null, accessToken: string | null) => {
    // Zaktualizuj lokalny stan
    setSocialAccounts(accounts =>
      accounts.map(acc =>
        acc.id === accountId
          ? { ...acc, meta_ad_account_id: adAccountId, meta_access_token: accessToken }
          : acc
      )
    );
  };

  const toggleAccountAutoBoost = async (accountId: string, currentValue: boolean) => {
    try {
      const { error } = await supabase
        .from('social_accounts')
        .update({ auto_boost_enabled: !currentValue })
        .eq('id', accountId);

      if (error) throw error;

      // Zaktualizuj lokalny stan
      setSocialAccounts(accounts =>
        accounts.map(acc =>
          acc.id === accountId
            ? { ...acc, auto_boost_enabled: !currentValue }
            : acc
        )
      );

      toast.success(!currentValue ? 'Auto-boost włączony' : 'Auto-boost wyłączony');
    } catch (error) {
      console.error('Error toggling auto-boost:', error);
      toast.error('Błąd podczas zmiany ustawienia');
    }
  };

  const testToken = async () => {
    try {
      setTesting(true);
      setTestResults(null);

      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        toast.error('Musisz być zalogowany');
        return;
      }

      toast.loading('Testowanie tokena...', { id: 'test' });

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/test-meta-ads-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            meta_access_token: settings.meta_access_token,
            meta_ad_account_id: settings.meta_ad_account_id,
            meta_app_id: settings.meta_app_id,
            meta_app_secret: settings.meta_app_secret,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to test token');
      }

      setTestResults(result);

      if (result.summary?.all_tests_passed) {
        toast.success('✅ Token jest poprawny!', { id: 'test' });
      } else {
        toast.error('❌ Token wymaga poprawy. Zobacz wyniki testów.', { id: 'test' });
      }
    } catch (error) {
      console.error('Error testing token:', error);
      toast.error(
        error instanceof Error ? error.message : 'Błąd podczas testowania tokena',
        { id: 'test' }
      );
    } finally {
      setTesting(false);
    }
  };

  const refreshToken = async () => {
    try {
      setRefreshing(true);
      toast.loading('Odświeżanie tokenu...', { id: 'refresh' });

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Musisz być zalogowany', { id: 'refresh' });
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/meta-ads-token-refresh`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ force_refresh_user: user?.id }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to refresh token');
      }

      toast.success('Token został odświeżony!', { id: 'refresh' });
      await fetchSettings();
    } catch (error) {
      console.error('Error refreshing token:', error);
      toast.error(
        error instanceof Error ? error.message : 'Błąd podczas odświeżania tokenu',
        { id: 'refresh' }
      );
    } finally {
      setRefreshing(false);
    }
  };

  const getTokenStatus = () => {
    if (!settings.meta_token_expires_at) {
      return { status: 'unknown', label: 'Nieznany', color: 'slate' };
    }

    const expiresAt = new Date(settings.meta_token_expires_at);
    const now = new Date();
    const daysLeft = Math.floor((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
      return { status: 'expired', label: 'Wygasł', color: 'red', daysLeft };
    } else if (daysLeft < 7) {
      return { status: 'expiring', label: `Wygasa za ${daysLeft} dni`, color: 'yellow', daysLeft };
    } else {
      return { status: 'active', label: `Ważny ${daysLeft} dni`, color: 'green', daysLeft };
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);

      // Przygotuj kopię settings do modyfikacji (nie mutujemy state bezpośrednio)
      let settingsToSave = { ...settings };

      // Walidacja Meta App credentials
      if (settingsToSave.meta_access_token && (!settingsToSave.meta_app_id || !settingsToSave.meta_app_secret)) {
        toast.error('Musisz podać Meta App ID i App Secret aby wymienić token');
        setSaving(false);
        return;
      }

      // Walidacja formatu Ad Account ID
      if (settingsToSave.meta_ad_account_id) {
        let adAccountId = settingsToSave.meta_ad_account_id.trim();

        // Jeśli zawiera "act=" zamień na "act_"
        if (adAccountId.includes('act=')) {
          adAccountId = adAccountId.replace('act=', 'act_');
          settingsToSave.meta_ad_account_id = adAccountId;
          toast.success('Format ID konta został automatycznie poprawiony (act= → act_)');
        }

        // Sprawdź czy format jest poprawny
        if (!adAccountId.startsWith('act_') && adAccountId.length > 0) {
          toast.error('ID konta reklamowego musi zaczynać się od "act_"');
          setSaving(false);
          return;
        }
      }

      // W tym systemie używamy wyłącznie Tokenów Użytkownika Systemowego (System User Token),
      // które są długoterminowe z definicji. Próba ich wymiany przez endpoint OAuth kończy się błędem 400 API Facebooka,
      // dlatego całkowicie usuwamy logikę wymiany tokena z zapisywania.
      
      const { error } = await supabase
        .from('ad_booster_settings')
        .upsert({
          user_id: user?.id,
          ...settingsToSave,
        }, {
          onConflict: 'user_id'
        });

      if (error) throw error;

      toast.success('Ustawienia zostały zapisane!');
      setTestResults(null);
      await fetchSettings(); // Odśwież dane aby pokazać zaktualizowany token
      onUpdate();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Błąd podczas zapisywania ustawień');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auto Promote Per Account */}
      <div className="bg-slate-900/90 rounded-xl p-6 border border-slate-700/50">
        <h2 className="text-xl font-bold text-white mb-4">Automatyzacja - Wybierz Konta</h2>
        <p className="text-sm text-slate-400 mb-4">
          Wybierz, dla których kont ma być włączone automatyczne uruchamianie kampanii reklamowych
        </p>

        {socialAccounts.length === 0 ? (
          <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 text-center">
            <p className="text-slate-400">Brak podłączonych kont Facebook/Instagram</p>
            <p className="text-xs text-slate-500 mt-1">
              Podłącz konta w sekcji Connected Accounts aby móc włączyć automatyzację
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Facebook Accounts */}
            {socialAccounts.filter(acc => acc.platform === 'facebook').length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <Facebook className="w-4 h-4" />
                  Facebook ({socialAccounts.filter(acc => acc.platform === 'facebook').length})
                </h3>
                <div className="space-y-1">
                  {socialAccounts
                    .filter(acc => acc.platform === 'facebook')
                    .map(account => (
                      <div
                        key={account.id}
                        className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/50 border border-slate-700/50"
                      >
                        <label className="flex items-center gap-3 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={account.auto_boost_enabled}
                            onChange={() => toggleAccountAutoBoost(account.id, account.auto_boost_enabled)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <p className="text-sm text-white">{account.page_name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <span>ID: {account.page_id}</span>
                              {account.meta_ad_account_id && (
                                <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 rounded border border-blue-500/30">
                                  <Key className="w-3 h-3 inline mr-1" />
                                  Własne Ad Account
                                </span>
                              )}
                            </div>
                          </div>
                          {account.auto_boost_enabled && (
                            <span className="text-xs px-2 py-1 bg-green-500/20 text-green-300 rounded border border-green-500/30">
                              Aktywne
                            </span>
                          )}
                        </label>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAccount(account);
                          }}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors group"
                          title="Konfiguruj Meta Ads dla tego konta"
                        >
                          <SettingsIcon className="w-4 h-4 text-slate-400 group-hover:text-white" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Instagram Accounts */}
            {socialAccounts.filter(acc => acc.platform === 'instagram').length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                  <Instagram className="w-4 h-4" />
                  Instagram ({socialAccounts.filter(acc => acc.platform === 'instagram').length})
                </h3>
                <div className="space-y-1">
                  {socialAccounts
                    .filter(acc => acc.platform === 'instagram')
                    .map(account => (
                      <div
                        key={account.id}
                        className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/50 border border-slate-700/50"
                      >
                        <label className="flex items-center gap-3 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={account.auto_boost_enabled}
                            onChange={() => toggleAccountAutoBoost(account.id, account.auto_boost_enabled)}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-pink-600 focus:ring-pink-500"
                          />
                          <div className="flex-1">
                            <p className="text-sm text-white">{account.page_name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <span>ID: {account.page_id}</span>
                              {account.meta_ad_account_id && (
                                <span className="px-1.5 py-0.5 bg-pink-500/20 text-pink-300 rounded border border-pink-500/30">
                                  <Key className="w-3 h-3 inline mr-1" />
                                  Własne Ad Account
                                </span>
                              )}
                            </div>
                          </div>
                          {account.auto_boost_enabled && (
                            <span className="text-xs px-2 py-1 bg-green-500/20 text-green-300 rounded border border-green-500/30">
                              Aktywne
                            </span>
                          )}
                        </label>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedAccount(account);
                          }}
                          className="p-2 hover:bg-slate-700 rounded-lg transition-colors group"
                          title="Konfiguruj Meta Ads dla tego konta"
                        >
                          <SettingsIcon className="w-4 h-4 text-slate-400 group-hover:text-white" />
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-xs text-blue-300">
            💡 Kampanie będą tworzone automatycznie dla postów opublikowanych na wybranych kontach po upływie {settings.delay_hours}h od publikacji
          </p>
        </div>
      </div>

      {/* Budget & Duration */}
      <div className="bg-slate-900/90 rounded-xl p-6 border border-slate-700/50">
        <h2 className="text-xl font-bold text-white mb-4">Domyślne Parametry Kampanii</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              <DollarSign className="w-4 h-4 inline mr-1" />
              Budżet dzienny (PLN)
            </label>
            <input
              type="number"
              min="1"
              step="0.5"
              value={settings.default_budget_daily}
              onChange={(e) => setSettings({ ...settings, default_budget_daily: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              <Calendar className="w-4 h-4 inline mr-1" />
              Czas trwania (dni)
            </label>
            <input
              type="number"
              min="1"
              max="30"
              value={settings.default_duration_days}
              onChange={(e) => setSettings({ ...settings, default_duration_days: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              <Clock className="w-4 h-4 inline mr-1" />
              Opóźnienie (godziny)
            </label>
            <input
              type="number"
              min="1"
              max="168"
              value={settings.delay_hours}
              onChange={(e) => setSettings({ ...settings, delay_hours: parseInt(e.target.value) })}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <p className="text-xs text-slate-400 mt-1">
              Czas oczekiwania przed startem promocji po publikacji
            </p>
          </div>
        </div>
      </div>

      {/* Targeting */}
      <div className="bg-slate-900/90 rounded-xl p-6 border border-slate-700/50">
        <h2 className="text-xl font-bold text-white mb-4">
          <Target className="w-5 h-5 inline mr-2" />
          Grupy Docelowe
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Faza 1 - Pierwsza Kampania
            </label>
            <div className="flex gap-2">
              <select
                value={settings.phase1_target_type}
                onChange={(e) => setSettings({ ...settings, phase1_target_type: e.target.value })}
                className="flex-1 px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="page_fans">Obserwujący fanpage / profil</option>
                <option value="lookalike">Lookalike (podobni do obserwujących)</option>
                <option value="custom">Własna grupa docelowa</option>
              </select>
              {settings.phase1_target_type === 'custom' && (
                <button
                  onClick={() => setTargetingModalPhase(1)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  title="Konfiguruj targetowanie"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Uruchamiana {settings.delay_hours}h po publikacji
            </p>
            {settings.phase1_target_type === 'custom' && (
              <div className="mt-2 p-2 bg-slate-800/50 rounded text-xs text-slate-300">
                {settings.phase1_targeting.age_min}-{settings.phase1_targeting.age_max} lat
                {' • '}
                {settings.phase1_targeting.geo_locations.countries.length} {settings.phase1_targeting.geo_locations.countries.length === 1 ? 'kraj' : 'krajów'}
                {' • '}
                {settings.phase1_targeting.genders.includes(0) ? 'Wszyscy' :
                 settings.phase1_targeting.genders.includes(1) && settings.phase1_targeting.genders.includes(2) ? 'M + K' :
                 settings.phase1_targeting.genders.includes(1) ? 'Mężczyźni' : 'Kobiety'}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Faza 2 - Druga Kampania
            </label>
            <div className="flex gap-2">
              <select
                value={settings.phase2_target_type}
                onChange={(e) => setSettings({ ...settings, phase2_target_type: e.target.value })}
                className="flex-1 px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="page_fans">Obserwujący fanpage / profil</option>
                <option value="lookalike">Lookalike (podobni do obserwujących)</option>
                <option value="custom">Własna grupa docelowa</option>
              </select>
              {settings.phase2_target_type === 'custom' && (
                <button
                  onClick={() => setTargetingModalPhase(2)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center gap-2"
                  title="Konfiguruj targetowanie"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Uruchamiana po zakończeniu Fazy 1
            </p>
            {settings.phase2_target_type === 'custom' && (
              <div className="mt-2 p-2 bg-slate-800/50 rounded text-xs text-slate-300">
                {settings.phase2_targeting.age_min}-{settings.phase2_targeting.age_max} lat
                {' • '}
                {settings.phase2_targeting.geo_locations.countries.length} {settings.phase2_targeting.geo_locations.countries.length === 1 ? 'kraj' : 'krajów'}
                {' • '}
                {settings.phase2_targeting.genders.includes(0) ? 'Wszyscy' :
                 settings.phase2_targeting.genders.includes(1) && settings.phase2_targeting.genders.includes(2) ? 'M + K' :
                 settings.phase2_targeting.genders.includes(1) ? 'Mężczyźni' : 'Kobiety'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Meta Ads API */}
      <div className="bg-slate-900/90 rounded-xl p-6 border border-slate-700/50">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">
            <Key className="w-5 h-5 inline mr-2" />
            Integracja Meta Ads API
          </h2>
        </div>

        <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-blue-300 mb-2">
            <strong>Wymagane:</strong> Stwórz aplikację Meta na{' '}
            <a
              href="https://developers.facebook.com/apps"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-blue-200"
            >
              developers.facebook.com
            </a>
          </p>
          <p className="text-xs text-blue-200">
            1. Przejdź do: Ustawienia {'>'} Podstawowe<br/>
            2. Skopiuj "Identyfikator aplikacji" i "Klucz tajny aplikacji"<br/>
            3. Dodaj uprawnienia: ads_management, pages_read_engagement, pages_manage_posts
          </p>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Meta App ID
              </label>
              <input
                type="text"
                placeholder="1234567890123456"
                value={settings.meta_app_id}
                onChange={(e) => setSettings({ ...settings, meta_app_id: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                autoComplete="off"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
              />
              <p className="text-xs text-slate-400 mt-1">
                Identyfikator aplikacji z Meta Developer Console
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">
                Meta App Secret
              </label>
              <input
                type="password"
                placeholder="abc123def456..."
                value={settings.meta_app_secret}
                onChange={(e) => setSettings({ ...settings, meta_app_secret: e.target.value })}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
                autoComplete="new-password"
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
              />
              <p className="text-xs text-slate-400 mt-1">
                Klucz tajny aplikacji (nie udostępniaj nikomu)
              </p>
            </div>
          </div>

          <div className="border-t border-slate-700 pt-4 mt-4">
            <p className="text-sm text-slate-300 mb-3 font-medium">Dane konta reklamowego</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              ID Konta Reklamowego
            </label>
            <input
              type="text"
              placeholder="act_1234567890"
              value={settings.meta_ad_account_id}
              onChange={(e) => setSettings({ ...settings, meta_ad_account_id: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono"
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
            />
            <p className="text-xs text-slate-400 mt-1">
              Format: <code className="bg-slate-700 px-1 rounded">act_XXXXXXXXXX</code> (z podkreśleniem, nie znakiem równości)
            </p>
            <p className="text-xs text-slate-400">
              Znajdziesz w <a href="https://business.facebook.com/settings/ad-accounts" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">Menadżerze Reklam</a>
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-slate-200">
                Token Dostępu Meta Ads
              </label>
              {settings.meta_token_expires_at && (() => {
                const tokenStatus = getTokenStatus();
                const colorClasses = {
                  green: 'bg-green-500/20 text-green-300 border-green-500/30',
                  yellow: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
                  red: 'bg-red-500/20 text-red-300 border-red-500/30',
                  slate: 'bg-slate-500/20 text-slate-300 border-slate-500/30'
                };
                return (
                  <span className={`px-2 py-1 rounded-lg text-xs border ${colorClasses[tokenStatus.color as keyof typeof colorClasses]}`}>
                    {tokenStatus.label}
                  </span>
                );
              })()}
            </div>
            <input
              type="password"
              placeholder="EAABsbCS1iHgBO..."
              value={settings.meta_access_token}
              onChange={(e) => setSettings({ ...settings, meta_access_token: e.target.value })}
              className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoComplete="new-password"
              data-1p-ignore
              data-lpignore="true"
              data-form-type="other"
            />
            {settings.meta_token_expires_at ? (
              <div className="mt-2 flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  Wygasa: {new Date(settings.meta_token_expires_at).toLocaleDateString('pl-PL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                {(() => {
                  const tokenStatus = getTokenStatus();
                  if (tokenStatus.status === 'expired' || tokenStatus.status === 'expiring') {
                    return (
                      <button
                        onClick={refreshToken}
                        disabled={refreshing}
                        className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white text-xs rounded-lg transition-colors"
                      >
                        <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
                        {refreshing ? 'Odświeżanie...' : 'Odśwież Token'}
                      </button>
                    );
                  }
                })()}
              </div>
            ) : (
              <p className="text-xs text-slate-400 mt-1">
                Token z uprawnieniami ads_management (zostanie automatycznie wymieniony na długotrwały)
              </p>
            )}

            {(() => {
              const tokenStatus = getTokenStatus();
              if (tokenStatus.status === 'expired') {
                return (
                  <div className="mt-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-red-300">
                      <p className="font-medium mb-1">Token wygasł</p>
                      <p>Kampanie nie mogą być uruchamiane. Kliknij "Odśwież Token" aby automatycznie odnowić lub wklej nowy token.</p>
                    </div>
                  </div>
                );
              } else if (tokenStatus.status === 'expiring') {
                return (
                  <div className="mt-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-yellow-300">
                      <p className="font-medium mb-1">Token wkrótce wygaśnie</p>
                      <p>Zalecamy odświeżenie tokenu aby uniknąć przerw w działaniu kampanii.</p>
                    </div>
                  </div>
                );
              }
            })()}
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-blue-300">
            💡 <strong>Informacja:</strong> Token dostępu możesz wygenerować w{' '}
            <a
              href="https://developers.facebook.com/tools/explorer/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-blue-200"
            >
              Graph API Explorer
            </a>
            {' '}z uprawnieniami: ads_management, pages_read_engagement, pages_manage_posts
          </p>
        </div>

        {/* Test Token Button */}
        <div className="mt-4">
          <button
            onClick={testToken}
            disabled={testing || !settings.meta_ad_account_id || !settings.meta_access_token}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            {testing ? 'Testowanie...' : 'Przetestuj Token i Uprawnienia'}
          </button>
          <p className="text-xs text-slate-400 mt-1">
            Sprawdzi czy token ma dostęp do konta reklamowego i wymagane uprawnienia
          </p>
        </div>

        {/* Test Results */}
        {testResults && (
          <div className="mt-4 space-y-2">
            <div className={`p-4 rounded-lg border ${
              testResults.summary?.all_tests_passed
                ? 'bg-green-500/10 border-green-500/30'
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <p className={`font-medium ${
                testResults.summary?.all_tests_passed ? 'text-green-300' : 'text-red-300'
              }`}>
                {testResults.summary?.recommendation}
              </p>
            </div>

            {/* Token Info */}
            {testResults.token_info && (
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-sm font-medium text-slate-200 mb-2">Informacje o tokenie:</p>
                <ul className="text-xs text-slate-300 space-y-1">
                  <li>✓ App ID: {testResults.token_info.app_id}</li>
                  <li>✓ Ważny: {testResults.token_info.is_valid ? 'Tak' : 'Nie'}</li>
                  {testResults.token_info.type && (
                    <li>✓ Typ: {testResults.token_info.type === 'PAGE' ? 'Page Token' : testResults.token_info.type === 'USER' ? 'User Token' : testResults.token_info.type}</li>
                  )}
                  {testResults.token_info.scopes && (
                    <li>✓ Uprawnienia: {testResults.token_info.scopes.join(', ')}</li>
                  )}
                  {testResults.token_info.expires_at_human && (
                    <li>✓ Wygasa: {testResults.token_info.expires_at_human}</li>
                  )}
                </ul>
              </div>
            )}

            {/* Ad Account Info */}
            {testResults.ad_account_name && (
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-sm font-medium text-slate-200 mb-2">Konto reklamowe:</p>
                <ul className="text-xs text-slate-300 space-y-1">
                  <li>✓ Nazwa: {testResults.ad_account_name}</li>
                  <li>✓ ID: {testResults.ad_account_id}</li>
                  <li>✓ Status: {testResults.ad_account_status === 1 ? 'Aktywne' : 'Nieaktywne'}</li>
                </ul>
              </div>
            )}

            {/* Available Accounts (only for USER tokens) */}
            {testResults.tests?.available_accounts?.success && testResults.tests.available_accounts.data?.data && (
              <div className="p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="text-sm font-medium text-slate-200 mb-2">Dostępne konta reklamowe (kliknij aby wybrać):</p>
                <ul className="text-xs text-slate-300 space-y-2">
                  {testResults.tests.available_accounts.data.data.map((acc: any) => (
                    <li key={acc.id} className="flex justify-between items-center p-2 rounded bg-slate-800/80 border border-slate-700 hover:border-indigo-500/50 transition-colors cursor-pointer" onClick={() => { setSettings({ ...settings, meta_ad_account_id: acc.id }); toast.success(`Wybrano konto: ${acc.name}`); }}>
                      <span>• {acc.name || acc.id} ({acc.account_status === 1 ? 'Aktywne' : 'Nieaktywne'})</span>
                      <span className="text-indigo-400 font-bold px-2 py-1 bg-indigo-500/20 rounded">Wybierz</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Errors - only show CRITICAL test failures */}
            {!testResults.summary?.all_tests_passed && (
              <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-sm font-medium text-red-300 mb-2">Problemy:</p>
                <ul className="text-xs text-red-200 space-y-1">
                  {['token_debug', 'ad_account_access', 'campaigns_read'].map((key) => {
                    const test = testResults.tests[key];
                    if (test && !test.success) {
                      const errorMsg = test.data?.error?.message || test.data?.error?.error_user_msg || test.error || 'Nieznany błąd';
                      const labels: Record<string, string> = {
                        token_debug: 'Walidacja tokenu',
                        ad_account_access: 'Dostęp do konta reklamowego',
                        campaigns_read: 'Odczyt kampanii (ads_management)',
                      };
                      return (
                        <li key={key}>
                          • {labels[key]}: {errorMsg}
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AutoWorker Settings */}
      <div className="bg-[#1e293b]/50 rounded-xl p-6 border border-indigo-500/30 mt-6">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center">
          <Clock className="w-5 h-5 inline mr-2 text-indigo-500" />
          AutoWorker (Skalowanie i Optymalizacja AI)
        </h2>
        
        <div className="mb-6 p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
          <p className="text-sm text-indigo-300 mb-2">
            AutoWorker co 48h optymalizuje Twoje kampanie reklamowe. Usuwa najsłabsze kreacje i tworzy nowe, bazując na najlepszych wynikach, korzystając z mocy Google Gemini 2.0 Flash (teksty) i Imagen 3 (zdjęcia reklamowe).
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-slate-800 p-4 rounded-xl border border-slate-700">
            <input
              type="checkbox"
              id="autoscale_enabled"
              checked={settings.autoscale_enabled}
              onChange={(e) => setSettings({ ...settings, autoscale_enabled: e.target.checked })}
              className="w-5 h-5 rounded bg-slate-700 border-slate-600 text-indigo-500 focus:ring-indigo-500"
            />
            <label htmlFor="autoscale_enabled" className="text-sm font-bold text-white cursor-pointer select-none">
              Włącz autoptymalizację i skalowanie kreacji co 48h
            </label>
          </div>

          {settings.autoscale_enabled && (
            <div className="space-y-4 animate-in fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-2">
                    🔑 Klucz API: Google Gemini (Tekst + Obrazy AI)
                  </label>
                  <input
                    type="password"
                    placeholder="AIzaSy..."
                    value={settings.image_api_key}
                    onChange={(e) => setSettings({ ...settings, image_api_key: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Główny klucz — generuje teksty kampanii (Gemini 2.0 Flash) i zdjęcia (Imagen 3). Pobierz z <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300 underline">Google AI Studio</a>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-300 mb-2">
                    🔑 Klucz API: Anthropic Claude (Opcjonalny Fallback)
                  </label>
                  <input
                    type="password"
                    placeholder="sk-ant-..."
                    value={settings.anthropic_key}
                    onChange={(e) => setSettings({ ...settings, anthropic_key: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white font-mono"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Alternatywny model AI — używany gdy Gemini niedostępny
                  </p>
                </div>
              </div>
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <p className="text-xs text-emerald-300">
                  ✅ <strong>Minimum:</strong> Podaj klucz Google Gemini — obsługuje generowanie tekstów kampanii, nagłówków, targetowania, oraz profesjonalnych zdjęć reklamowych (Imagen 3).
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* WooCommerce REST API */}
      <WooCommerceSettings />

      {/* Save Button */}
      <div className="flex justify-end mt-6">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 disabled:from-slate-700 disabled:to-slate-700 text-white rounded-lg font-medium transition-colors shadow-lg"
        >
          <Save className="w-5 h-5" />
          {saving ? 'Zapisywanie...' : 'Zapisz Ustawienia'}
        </button>
      </div>

      {/* Account Meta Ads Modal */}
      {selectedAccount && (
        <AccountMetaAdsModal
          account={selectedAccount}
          onClose={() => setSelectedAccount(null)}
          onSave={handleAccountMetaAdsSave}
        />
      )}

      {/* Advanced Targeting Modal */}
      {targetingModalPhase && (
        <AdvancedTargetingModal
          phase={targetingModalPhase}
          initialTargeting={targetingModalPhase === 1 ? settings.phase1_targeting : settings.phase2_targeting}
          onClose={() => setTargetingModalPhase(null)}
          onSave={(targeting) => {
            if (targetingModalPhase === 1) {
              setSettings({ ...settings, phase1_targeting: targeting });
            } else {
              setSettings({ ...settings, phase2_targeting: targeting });
            }
            setTargetingModalPhase(null);
          }}
        />
      )}
    </div>
  );
}
